import { Controller } from "@hotwired/stimulus"

import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  Link,
  BlockQuote,
  Alignment,
  Highlight,
  Base64UploadAdapter,
  Image,
  ImageCaption,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Clipboard,
  SourceEditing,
  Table,
  TableToolbar,
  Markdown,
  PasteFromMarkdownExperimental,
  Code,
  CodeBlock,
  HorizontalLine,
  MediaEmbed,
  RemoveFormat,
  Indent,
  IndentBlock
} from 'ckeditor5'

import translations from 'ckeditor5/translations/pt-br.js'

export default class extends Controller {
  static values = {
    mode: { type: String, default: 'full' },
    placeholder: { type: String, default: 'Digite seu conteúdo aqui...' },
    minHeight: { type: Number, default: 160 }
  }

  connect() {
    const isFull = this.modeValue === 'full'

    const config = {
      licenseKey: 'GPL',
      language: 'pt-br',
      placeholder: this.placeholderValue,
      translations: [translations],

      plugins: [
        Essentials,
        Paragraph,
        Heading,
        Bold,
        Italic,
        Underline,
        Strikethrough,
        List,
        Link,
        BlockQuote,
        Alignment,
        Highlight,

        Clipboard,
        Image,
        ImageCaption,
        ImageStyle,
        ImageToolbar,
        ImageUpload,
        Base64UploadAdapter,

        Markdown,
        PasteFromMarkdownExperimental,
        Code,
        RemoveFormat,

        ...(isFull ? [
          SourceEditing,
          Table,
          TableToolbar,
          CodeBlock,
          HorizontalLine,
          MediaEmbed,
          Indent,
          IndentBlock
        ] : [])
      ],

      toolbar: {
        items: [
          'undo', 'redo', '|',
          ...(isFull ? ['heading', '|'] : []),

          'bold', 'italic', 'underline', 'strikethrough', 'code', 'removeFormat',
          '|',

          'link',
          'imageUpload',

          ...(isFull ? ['mediaEmbed', 'insertTable', 'horizontalLine'] : []),
          '|',

          'bulletedList',
          'numberedList',
          ...(isFull ? ['outdent', 'indent'] : []),
          '|',

          'alignment',
          'blockQuote',
          'highlight',

          ...(isFull ? ['codeBlock', '|', 'sourceEditing'] : [])
        ],
        shouldNotGroupWhenFull: true
      },

      heading: {
        options: isFull ? [
          { model: 'paragraph', title: 'Normal', class: 'ck-heading_paragraph' },
          { model: 'heading1', view: 'h1', title: 'Título 1', class: 'ck-heading_heading1' },
          { model: 'heading2', view: 'h2', title: 'Título 2', class: 'ck-heading_heading2' },
          { model: 'heading3', view: 'h3', title: 'Título 3', class: 'ck-heading_heading3' }
        ] : []
      },

      image: {
        toolbar: [
          'imageTextAlternative',
          '|',
          'imageStyle:inline',
          'imageStyle:block',
          'imageStyle:wrapText'
        ],
        upload: {
          types: ['png', 'jpeg', 'jpg', 'gif', 'webp']
        }
      },

      table: isFull ? {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
      } : undefined
    }

    const element = this.element.tagName === 'TEXTAREA'
      ? this.element
      : this.element.querySelector('textarea')

    if (!element) {
      console.error('CKEditor: textarea não encontrado')
      return
    }

    ClassicEditor
      .create(element, config)
      .then(editor => {
        this.editor = editor

        editor.editing.view.change(writer => {
          writer.setStyle(
            'min-height',
            `${this.minHeightValue}px`,
            editor.editing.view.document.getRoot()
          )
        })
      })
      .catch(error => {
        console.error('Erro ao iniciar CKEditor:', error)
      })
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
  }
}